import type { AssetType } from '@cartwhl/common/api/getAssets';
import type { MascotJobResponse } from '@cartwhl/common/api/getMascot';
import type { MotionRecord } from '@cartwhl/common/api/getMotionsByGroup';

import type { AssetLibraryDisplayItem } from './utils';
import { shouldShowStackedPreview } from './utils';
import { isProjectGroupID } from './assetLibraryGroupIds';

export type AssetLibraryGroupKind = 'mascot' | 'mogen-batch' | 'project';

export type AssetLibraryBatchGroupAssetType = Extract<
  AssetType,
  'MOGEN_JOB' | 'MASCOT_JOB'
>;

export type AssetLibraryGroupDetailSelection = {
  groupID: string;
  groupName: string;
  assetType: AssetLibraryBatchGroupAssetType;
  groupKind: AssetLibraryGroupKind;
};

export type AssetLibraryGroupDetailItem = {
  id: string;
  name: string;
  previewURL: string | null;
  createdAt?: string;
  createdBy?: string;
};

export type AssetLibraryGroupDetailLoadState = {
  status: 'initial' | 'pending' | 'ready' | 'error';
  items: AssetLibraryGroupDetailItem[];
  projectMotions: MotionRecord[];
  nextToken?: string;
  hasMore: boolean;
  error?: string;
  isLoadingMore: boolean;
};

export const INITIAL_GROUP_DETAIL_LOAD_STATE: AssetLibraryGroupDetailLoadState =
  {
    status: 'initial',
    items: [],
    projectMotions: [],
    hasMore: false,
    isLoadingMore: false,
  };

export function getAssetLibraryGroupKind(input: {
  assetType: AssetType;
  groupID?: string;
}): AssetLibraryGroupKind {
  if (input.assetType === 'MASCOT_JOB') {
    return 'mascot';
  }

  if (input.groupID && isProjectGroupID(input.groupID)) {
    return 'project';
  }

  return 'mogen-batch';
}

export function toGroupDetailSelection(
  asset: AssetLibraryDisplayItem,
): AssetLibraryGroupDetailSelection | null {
  if (!shouldShowStackedPreview(asset) || !asset.groupID) {
    return null;
  }

  return {
    groupID: asset.groupID,
    groupName:
      asset.groupName?.trim() || asset.assetName?.trim() || 'Untitled batch',
    assetType: asset.assetType as AssetLibraryBatchGroupAssetType,
    groupKind: getAssetLibraryGroupKind(asset),
  };
}

export function toGroupDetailSelectionFromAsset(
  asset: AssetLibraryDisplayItem,
): AssetLibraryGroupDetailSelection | null {
  const selection = toGroupDetailSelection(asset);
  if (selection) {
    return selection;
  }

  if (!asset.groupID) {
    return null;
  }

  return {
    groupID: asset.groupID,
    groupName: asset.groupName?.trim() || asset.assetName?.trim() || 'Untitled',
    assetType: asset.assetType as AssetLibraryBatchGroupAssetType,
    groupKind: getAssetLibraryGroupKind(asset),
  };
}

export function toMotionGroupDetailItem(
  motion: MotionRecord,
): AssetLibraryGroupDetailItem {
  return {
    id: motion.jobID,
    name: motion.title?.trim() || motion.jobID,
    previewURL: motion.studioPreviewURL?.trim() || null,
  };
}

export function toMascotGroupDetailItem(
  mascot: MascotJobResponse,
): AssetLibraryGroupDetailItem {
  return {
    id: mascot.mascotID,
    name: mascot.mascotName?.trim() || mascot.mascotID,
    previewURL: mascot.outputVideoURL?.trim() || null,
  };
}

export function formatGroupDetailItemTitle(
  index: number,
  name: string,
): string {
  return `${String(index + 1).padStart(2, '0')}. ${name}`;
}

export function toGroupDetailItemDisplayAsset(
  item: AssetLibraryGroupDetailItem,
  selection: Pick<
    AssetLibraryGroupDetailSelection,
    'groupID' | 'groupName' | 'assetType'
  >,
): AssetLibraryDisplayItem {
  const timestamp = item.createdAt ?? new Date(0).toISOString();

  return {
    assetID: item.id,
    assetName: item.name,
    assetType: selection.assetType,
    groupID: selection.groupID,
    groupName: selection.groupName,
    thumbnailURL: item.previewURL,
    createdAt: timestamp,
    createdBy: item.createdBy ?? '',
    createdByUser: item.createdBy ?? '',
    lastUpdatedAt: timestamp,
  };
}
